import React, { useState } from 'react';
import {
  BookOpen, ClipboardList, Table, Printer, Trash2, Pencil, FolderInput, X,
  Download, Loader2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PrintHeader from '@/components/PrintHeader';
import PdfCoverDialog from '@/components/PdfCoverDialog';
import { useDownloadPdf } from '@/hooks/useDownloadPdf';

const TYPE_META = {
  lesson_plan: { label: 'Lesson plan', icon: BookOpen },
  skeleton_notes: { label: 'Skeleton notes', icon: ClipboardList },
  rubric: { label: 'Rubric', icon: Table },
};

const Section = ({ heading, children }) => (
  <section className="mb-5">
    <h4 className="text-xs uppercase tracking-[0.14em] text-[#D4AF37] font-semibold mb-2">{heading}</h4>
    {children}
  </section>
);

const BulletList = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1 text-sm text-[#2D3748]">
    {items.map((item, idx) => <li key={`${idx}-${item.slice(0, 40)}`}>{item}</li>)}
  </ul>
);

const LessonPlanContent = ({ content }) => (
  <>
    {content.objectives?.length > 0 && <Section heading="Objectives"><BulletList items={content.objectives} /></Section>}
    {content.materials?.length > 0 && <Section heading="Materials"><BulletList items={content.materials} /></Section>}
    {content.duration && <Section heading="Duration"><p className="text-sm text-[#2D3748]">{content.duration}</p></Section>}
    {content.instructions && (
      <Section heading="Instructions">
        <p className="text-sm text-[#2D3748] whitespace-pre-wrap leading-relaxed">{content.instructions}</p>
      </Section>
    )}
    {content.assessment_criteria?.length > 0 && (
      <Section heading="Assessment criteria"><BulletList items={content.assessment_criteria} /></Section>
    )}
  </>
);

const SkeletonNotesContent = ({ content }) => (
  <>
    {content.sections?.map((s, idx) => (
      <Section key={`${idx}-${s.title || ''}`} heading={s.title}>
        {s.student_content && (
          <div className="mb-3 bg-[#F9F8F4] border-l-2 border-[#7A93AC] pl-3 py-2">
            <p className="text-[11px] uppercase tracking-wider text-[#5A6577] font-semibold mb-1">Student</p>
            <p className="text-sm text-[#2D3748] whitespace-pre-wrap">{s.student_content}</p>
          </div>
        )}
        {s.teacher_notes && (
          <div className="bg-[#FBF6E8] border-l-2 border-[#D4AF37] pl-3 py-2">
            <p className="text-[11px] uppercase tracking-wider text-[#8A6D1B] font-semibold mb-1">Teacher</p>
            <p className="text-sm text-[#2D3748] whitespace-pre-wrap">{s.teacher_notes}</p>
          </div>
        )}
      </Section>
    ))}
  </>
);

const RubricContent = ({ content }) => (
  <>
    {content.description && (
      <p className="text-sm text-[#5A6577] italic mb-4">{content.description}</p>
    )}
    <div className="overflow-x-auto">
      <table className="w-full text-xs border border-[#EBE7E0]">
        <thead className="bg-[#F9F8F4]">
          <tr>
            <th className="text-left p-2 border border-[#EBE7E0] font-semibold text-[#14213D]">Criterion</th>
            {content.criteria?.[0]?.levels?.map((lvl, idx) => (
              <th key={`${idx}-${lvl.label || lvl.score}`} className="text-left p-2 border border-[#EBE7E0] font-semibold text-[#14213D]">
                {lvl.label} ({lvl.score})
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {content.criteria?.map((c, idx) => (
            <tr key={`${idx}-${c.name || ''}`}>
              <td className="p-2 border border-[#EBE7E0] font-semibold text-[#14213D]">{c.name}</td>
              {c.levels?.map((lvl, li) => (
                <td key={`${li}-${lvl.label || lvl.score}`} className="p-2 border border-[#EBE7E0] text-[#2D3748]">{lvl.description}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {content.total_points && (
      <p className="text-xs text-[#5A6577] mt-3">Total possible: <strong>{content.total_points}</strong></p>
    )}
  </>
);

const ARTIFACT_RENDERERS = {
  lesson_plan: LessonPlanContent,
  skeleton_notes: SkeletonNotesContent,
  rubric: RubricContent,
};

// Exported for the printable-lesson route
export { ARTIFACT_RENDERERS, TYPE_META };

export default function LessonViewerModal({ lesson, folders, onClose, onUpdate, onDelete }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [coverOpen, setCoverOpen] = useState(false);
  const { downloading: downloadingPdf, downloadPdf } = useDownloadPdf();

  if (!lesson) return null;

  const meta = TYPE_META[lesson.artifact_type] || TYPE_META.lesson_plan;
  const Icon = meta.icon;
  const Renderer = ARTIFACT_RENDERERS[lesson.artifact_type] || LessonPlanContent;
  const folder = lesson.folder_id ? folders.find((f) => f.id === lesson.folder_id) : null;

  const startRename = () => { setTitleDraft(lesson.title); setEditingTitle(true); };
  const saveRename = async () => {
    const t = titleDraft.trim();
    if (!t || t === lesson.title) { setEditingTitle(false); return; }
    await onUpdate(lesson.id, { title: t });
    setEditingTitle(false);
  };

  const handlePrint = () => window.print();

  return (
    <Dialog open={!!lesson} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 flex flex-col print:max-w-none print:max-h-none print:shadow-none print:border-0" data-testid="lesson-viewer">
        <div className="px-6 pt-4">
          <PrintHeader
            resourceType={meta.label}
            resourceTitle={lesson.title}
            grade={lesson.grade_level}
          />
        </div>
        <DialogHeader className="p-6 pb-4 border-b border-[#EBE7E0] flex-shrink-0 print:hidden">
          <div className="flex items-start gap-3 pr-8">
            <div className="w-10 h-10 rounded-md bg-[#F9F8F4] border border-[#EBE7E0] flex items-center justify-center flex-shrink-0">
              <Icon className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-[11px] uppercase tracking-[0.16em] text-[#D4AF37] font-semibold">{meta.label}</span>
                {lesson.grade_level && (
                  <span className="text-[11px] uppercase tracking-wider text-[#5A6577]">Grade {lesson.grade_level}</span>
                )}
                {folder && (
                  <span className="text-[11px] text-[#5A6577] inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: folder.color }} /> {folder.name}
                  </span>
                )}
              </div>
              {editingTitle ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                    className="text-lg font-serif-display h-9"
                    autoFocus
                    data-testid="lesson-title-input"
                  />
                  <Button size="sm" onClick={saveRename} className="bg-[#14213D] text-[#FDFBF7]" data-testid="lesson-title-save">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingTitle(false)}>Cancel</Button>
                </div>
              ) : (
                <DialogTitle className="font-serif-display text-xl sm:text-2xl text-[#14213D] leading-tight break-words">
                  {lesson.title}
                </DialogTitle>
              )}
              {lesson.unit_title && !editingTitle && (
                <p className="text-xs text-[#5A6577] mt-1 italic truncate">{lesson.unit_title}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4 flex-wrap print:hidden">
            <Button variant="outline" size="sm" onClick={startRename} data-testid="viewer-rename-btn">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Rename
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="viewer-print-btn">
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCoverOpen(true)}
              disabled={downloadingPdf}
              data-testid="viewer-download-pdf-btn"
              title="Download this saved lesson as a PDF"
            >
              {downloadingPdf ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> PDF&hellip;</>
              ) : (
                <><Download className="h-3.5 w-3.5 mr-1.5" /> PDF</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(lesson)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              data-testid="viewer-delete-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6" data-testid="viewer-content">
          {lesson.description && (
            <p className="text-sm text-[#5A6577] mb-6 leading-relaxed italic">{lesson.description}</p>
          )}
          <Renderer content={lesson.content || {}} />
        </div>
      </DialogContent>
      <PdfCoverDialog
        open={coverOpen}
        onOpenChange={setCoverOpen}
        onConfirm={async (cover) => {
          const filename = `${(lesson.title || 'lesson').replace(/[^a-z0-9-]+/gi, '-').toLowerCase().slice(0, 40)}.pdf`;
          await downloadPdf({
            resource: 'library-lesson',
            resourceId: lesson.id,
            filename,
            cover: {
              ...cover,
              title: lesson.title,
              grade: lesson.grade_level ? String(lesson.grade_level) : '',
              resource_label: meta.label,
              subtitle: lesson.unit_title || '',
            },
          });
          setCoverOpen(false);
        }}
        downloading={downloadingPdf}
        context={{
          title: lesson.title,
          resource_label: meta.label,
          grade: lesson.grade_level,
        }}
      />
    </Dialog>
  );
}
